/**
 * Visitor session — localStorage-backed identity + bookmarks + visited log + notes.
 * No backend account required. Data is per-browser.
 */
import { useEffect, useState, useCallback } from 'react';

const KEYS = {
  name: 'visitor_name',
  bookmarks: 'visitor_bookmarks',    // Set<boothId>
  visited: 'visitor_visited',        // { [boothId]: ISO timestamp }
  notes: 'visitor_notes',            // { [boothId]: string }
};

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

// Broadcast changes across hook instances within the same tab
const listeners = new Set();
function notify() { listeners.forEach((fn) => fn(Date.now())); }

export function useVisitor() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    listeners.add(setTick);
    return () => { listeners.delete(setTick); };
  }, []);

  const name = readJSON(KEYS.name, '');
  const bookmarks = readJSON(KEYS.bookmarks, []);      // number[]
  const visited = readJSON(KEYS.visited, {});          // {id: iso}
  const notes = readJSON(KEYS.notes, {});              // {id: text}

  const setName = useCallback((n) => {
    writeJSON(KEYS.name, n);
    notify();
  }, []);

  const toggleBookmark = useCallback((boothId) => {
    const list = readJSON(KEYS.bookmarks, []);
    const idx = list.indexOf(boothId);
    if (idx >= 0) list.splice(idx, 1); else list.push(boothId);
    writeJSON(KEYS.bookmarks, list);
    notify();
    return idx < 0; // true if just added
  }, []);

  const isBookmarked = useCallback((boothId) => {
    return readJSON(KEYS.bookmarks, []).includes(boothId);
  }, []);

  const logVisit = useCallback((boothId) => {
    const map = readJSON(KEYS.visited, {});
    map[boothId] = new Date().toISOString();
    writeJSON(KEYS.visited, map);
    notify();
  }, []);

  const setNote = useCallback((boothId, text) => {
    const map = readJSON(KEYS.notes, {});
    if (text && text.trim()) map[boothId] = text;
    else delete map[boothId];
    writeJSON(KEYS.notes, map);
    notify();
  }, []);

  const clearAll = useCallback(() => {
    Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
    notify();
  }, []);

  // `tick` intentionally used to force re-render on notify
  void tick;

  return {
    name,
    setName,
    bookmarks,
    isBookmarked,
    toggleBookmark,
    visited,
    logVisit,
    notes,
    setNote,
    clearAll,
  };
}
